// src/components/Dashboard.jsx
import { useEffect, useState } from "react";
import {
    db,
    collection,
    query,
    onSnapshot,
    orderBy,
    addDoc,
    updateDoc,
    where,
    getDocs,
} from "./firebase";
import ConversationCard from "./ConversationCard";
import ChatPreviewItem from "./ChatPreviewItem";
import ChatWindow from "./ChatWindow";
import { needAttention as checkNeedAttention } from "./utils/chatHelpers";
import { useChatSource } from "./context/ChatSourceContext";
import { toast } from "react-toastify";
import { sendInfobipTemplate } from "./utils/sendInfobipTemplate";

// 👇 Ajustá estos nombres si tus colecciones se llaman distinto
const COLLECTIONS_BY_PROFILE = {
    consumidor: "activeUsersConsumidorFinal",
    mayorista: "activeUsersMayorista",
};

/**
 * Normaliza un teléfono cualquiera a formato "549XXXXXXXXX"
 */
function normalizePhoneTo549(raw = "") {
    const digits = String(raw).replace(/\D/g, ""); // solo dígitos

    if (!digits) return "";

    if (digits.startsWith("549")) {
        return digits;
    }

    if (digits.startsWith("54")) {
        const rest = digits.slice(2).replace(/^0+/, "");
        return "549" + rest;
    }

    const cleaned = digits.replace(/^0+/, "");
    return "549" + cleaned;
}

// 🔹 Utilidad para tomar N elementos aleatorios de un array
function sampleRandom(arr, n) {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, n);
}

export default function Dashboard() {
    const { source, setSource, currentCollection } = useChatSource();
    const [conversations, setConversations] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [view, setView] = useState(() => localStorage.getItem("chatView") || "grid");

    // NUEVA CONVERSACIÓN
    const [showNewConvModal, setShowNewConvModal] = useState(false);
    const [newPhone, setNewPhone] = useState("");
    const [newTemplate, setNewTemplate] = useState("nueva_conversacion_phiphitoys");
    const [newFromProfile, setNewFromProfile] = useState("consumidor");

    // ACCIÓN MASIVA / SORTEO
    const [showMassActionModal, setShowMassActionModal] = useState(false);
    const [sampledClients, setSampledClients] = useState([]);
    const [winnersRevealed, setWinnersRevealed] = useState(false);
    const [loadingWinners, setLoadingWinners] = useState(false);

    // MENSAJE MASIVO
    const [showMassMessageModal, setShowMassMessageModal] = useState(false);
    const [massMessageText, setMassMessageText] = useState("");
    const [massMessageSending, setMassMessageSending] = useState(false);
    // const [massMessageTestMode, setMassMessageTestMode] = useState(true); // true = 2 números hardcodeados

    const total = conversations.length;
    const botControl = conversations.filter((c) => !c.agentControl).length;
    const agentActive = total - botControl;
    const awaiting = conversations.filter((c) =>
        checkNeedAttention(c.historyChat, c.agentControl)
    ).length;

    const selectedConv = conversations.find((c) => c.id === selectedId);
    const toggleView = () => setView((v) => (v === "grid" ? "chat" : "grid"));

    useEffect(() => {
        localStorage.setItem("chatView", view);
    }, [view]);

    // Suscripción según colección elegida en el Context (últimos 30 días)
    useEffect(() => {
        const now = Date.now();
        const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
        const cutoff = now - THIRTY_DAYS_MS;

        const q = query(
            collection(db, currentCollection),
            where("lastUpdate", ">=", cutoff),
            orderBy("lastUpdate", "desc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setConversations(data);
        });
        setSelectedId(null);
        return () => unsubscribe();
    }, [currentCollection]);

    useEffect(() => {
        if (!selectedId && conversations.length) setSelectedId(conversations[0].id);
    }, [conversations, selectedId]);

    const openNewConvModal = () => {
        setNewFromProfile(source === "mayorista" ? "mayorista" : "consumidor");
        setNewTemplate("nueva_conversacion_phiphitoys");
        setShowNewConvModal(true);
    };

    const handleSendNewConversation = async () => {
        const rawPhone = newPhone.trim();
        if (!rawPhone) {
            toast.error("Ingresá el número del cliente");
            return;
        }

        const normalizedPhone = normalizePhoneTo549(rawPhone);
        if (!normalizedPhone) {
            toast.error("El número de teléfono no es válido");
            return;
        }

        const from =
            newFromProfile === "mayorista" ? "12134250460" : "5491168404947";

        const targetCollectionName = COLLECTIONS_BY_PROFILE[newFromProfile];
        if (!targetCollectionName) {
            toast.error("No se encontró la colección para ese perfil");
            return;
        }

        try {
            const result = await sendInfobipTemplate({
                to: normalizedPhone,
                from,
                templateName: newTemplate,
                language: "en",
            });

            if (!result.ok) {
                console.error("Error Infobip nueva conv:", result);
                toast.error(result.error || "No se pudo enviar el template");
                return;
            }

            const now = Date.now();
            const templateLabel = `[TEMPLATE ENVIADO] ${newTemplate}`;
            const targetColRef = collection(db, targetCollectionName);

            const existingSnap = await getDocs(
                query(targetColRef, where("personId", "==", normalizedPhone))
            );

            if (!existingSnap.empty) {
                const docRef = existingSnap.docs[0].ref;
                const existingData = existingSnap.docs[0].data() || {};
                const history = Array.isArray(existingData.historyChat)
                    ? existingData.historyChat
                    : [];

                await updateDoc(docRef, {
                    personId: normalizedPhone,
                    historyChat: [...history, { agent: templateLabel, timestamp: now }],
                    lastUpdate: now,
                    agentControl: true,
                    senderProfile: newFromProfile,
                    conversationStatus:
                        existingData.conversationStatus || "Nueva conversación",
                });
            } else {
                await addDoc(targetColRef, {
                    personId: normalizedPhone,
                    personName: normalizedPhone,
                    personFullName: normalizedPhone,
                    channel: "whatsapp",
                    createdAt: now,
                    lastUpdate: now,
                    agentControl: true,
                    conversationStatus: "Nueva conversación",
                    senderProfile: newFromProfile,
                    historyChat: [{ agent: templateLabel, timestamp: now }],
                });
            }

            toast.success(
                `Template enviado y conversación guardada en ${targetCollectionName}`
            );
            setShowNewConvModal(false);
            setNewPhone("");
        } catch (err) {
            console.error(err);
            toast.error("Hubo un error al iniciar la nueva conversación");
        }
    };

    // 🔹 Lógica del botón "Acción masiva" (sorteo)
    const handleMassAction = () => {
        const now = Date.now();
        const EIGHTEEN_HOURS_MS = 18 * 60 * 60 * 1000;
        const cutoff = now - EIGHTEEN_HOURS_MS;

        const candidates = conversations.filter((c) => {
            const lu = c.lastUpdate;
            return typeof lu === "number" && lu >= cutoff;
        });

        if (!candidates.length) {
            toast.info("No hay conversaciones en las últimas 18 horas.");
            return;
        }

        const selected = sampleRandom(candidates, 10);

        const mapped = selected.map((c) => {
            const name =
                c.personFullName ||
                c.personName ||
                c.personId ||
                "(sin nombre)";
            const phone = c.personId || "";
            return {
                id: c.id,
                name,
                phone,
            };
        });

        setSampledClients(mapped);
        setWinnersRevealed(false);
        setLoadingWinners(false);
        setShowMassActionModal(true);
    };

    // 🔹 Abrir modal de MENSAJE MASIVO
    const openMassMessageModal = () => {
        setMassMessageText(
            "Recordá que el sorteo será presencial a las 19hs en el Stand de Phi Phi Toys! 😊"
        );
        setMassMessageSending(false);
        setShowMassMessageModal(true);
    };


    // 🔹 Enviar mensaje masivo (por ahora simulado)
    const handleSendMassMessage = async () => {
        const text = massMessageText.trim();
        if (!text) {
            toast.error("Escribí el mensaje a enviar");
            return;
        }

        // 👉 SIEMPRE: todas las personas con actividad en las últimas 18 horas
        const now = Date.now();
        const EIGHTEEN_HOURS_MS = 18 * 60 * 60 * 1000;
        const cutoff = now - EIGHTEEN_HOURS_MS;

        const candidates = conversations.filter((c) => {
            const lu = c.lastUpdate;
            return typeof lu === "number" && lu >= cutoff;
        });

        const numbers = candidates
            .map((c) => c.personId)
            .filter(Boolean);

        const phones = Array.from(new Set(numbers)); // únicos

        if (!phones.length) {
            toast.info("No se encontraron números para enviar el mensaje.");
            return;
        }

        setMassMessageSending(true);
        try {
            const resp = await fetch(
                "https://sendmasswhatsapptext-qwa7hlejia-uc.a.run.app/",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        phones,
                        text,
                        // opcional: si querés elegir el sender según perfil:
                        // from: source === "mayorista" ? "12134250460" : "5491168404947",
                    }),
                }
            );

            const data = await resp.json();

            if (!resp.ok || !data.ok) {
                console.error("Error sendMassWhatsAppText", data);
                let msg = "Error al enviar el mensaje masivo";
                if (data.details && data.details.length && data.details[0].error) {
                    const err0 = data.details[0].error;
                    const detailText =
                        err0?.requestError?.serviceException?.text ||
                        err0?.message ||
                        JSON.stringify(err0);
                    msg += `: ${detailText}`;
                }
                toast.error(msg);
                return;
            }

            toast.success(
                `Mensaje masivo enviado a ${data.totalAttempted} número(s)`
            );
            setShowMassMessageModal(false);
        } catch (err) {
            console.error(err);
            toast.error("Hubo un error al enviar el mensaje masivo");
        } finally {
            setMassMessageSending(false);
        }
    };


    const hasModalOpen =
        showNewConvModal || showMassActionModal || showMassMessageModal;

    return (
        <>
            {/* Contenido principal, se blurea si hay modal abierto */}
            <div
                className={
                    hasModalOpen
                        ? "blur-sm pointer-events-none select-none"
                        : ""
                }
            >
                <div className="flex items-center justify-between mb-4 gap-2">
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-semibold">Conversaciones</h1>
                        <div className="join">
                            <button
                                className={`btn btn-sm join-item ${source === "mayorista" ? "btn-primary" : ""
                                    }`}
                                onClick={() => setSource("mayorista")}
                            >
                                Mayorista
                            </button>
                            <button
                                className={`btn btn-sm join-item ${source === "consumidor" ? "btn-primary" : ""
                                    }`}
                                onClick={() => setSource("consumidor")}
                            >
                                Consumidor Final
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button className="btn btn-sm" onClick={toggleView}>
                            {view === "grid" ? "Vista chat" : "Vista tarjetas"}
                        </button>

                        {/* Botón Acción masiva (sorteo) */}
                        <button
                            className="btn btn-sm btn-secondary"
                            onClick={handleMassAction}
                        >
                            Acción masiva
                        </button>

                        {/* Botón Mensaje masivo */}
                        <button
                            className="btn btn-sm btn-accent"
                            onClick={openMassMessageModal}
                        >
                            Mensaje masivo
                        </button>

                        <button
                            className="btn btn-sm btn-primary"
                            onClick={openNewConvModal}
                        >
                            Nueva conversación
                        </button>
                    </div>
                </div>

                {/* Aviso de rango de fechas */}
                <div className="alert alert-info shadow mb-3">
                    <div>
                        <span className="font-semibold">Nota:</span>
                        <span className="ml-2 text-sm">
                            Se muestran solo las conversaciones con actividad en los últimos 30 días.
                        </span>
                    </div>
                </div>

                <div className="stats shadow bg-base-100 w-full mb-4">
                    <div className="stat flex-1">
                        <div className="stat-title">Total</div>
                        <div className="stat-value">{total}</div>
                    </div>
                    <div className="stat flex-1">
                        <div className="stat-title">Bot</div>
                        <div className="stat-value text-secondary">{botControl}</div>
                    </div>
                    <div className="stat flex-1">
                        <div className="stat-title">Agente</div>
                        <div className="stat-value text-success">{agentActive}</div>
                    </div>
                    <div className="stat flex-1">
                        <div className="stat-title">Necesitan&nbsp;atención</div>
                        <div className="stat-value text-warning">{awaiting}</div>
                    </div>
                </div>

                {view === "grid" && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {conversations.map((c) => (
                            <ConversationCard
                                key={c.id}
                                conversation={c}
                                collectionName={currentCollection}
                            />
                        ))}
                    </div>
                )}

                {view === "chat" && (
                    <div className="flex h-[80vh] shadow rounded-lg overflow-hidden">
                        <aside className="w-72 border-r overflow-y-auto bg-base-200">
                            {conversations.map((c) => (
                                <ChatPreviewItem
                                    key={c.id}
                                    conversation={c}
                                    active={c.id === selectedId}
                                    onClick={() => setSelectedId(c.id)}
                                />
                            ))}
                        </aside>
                        <section className="flex-1 flex flex-col">
                            {selectedConv ? (
                                <ChatWindow
                                    conversation={selectedConv}
                                    collectionName={currentCollection}
                                />
                            ) : (
                                <div className="m-auto text-gray-500">
                                    Selecciona un chat para comenzar
                                </div>
                            )}
                        </section>
                    </div>
                )}
            </div>

            {/* Modal NUEVA CONVERSACIÓN */}
            {showNewConvModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4">
                            Iniciar nueva conversación
                        </h3>

                        <label className="label">
                            <span className="label-text">Teléfono del cliente</span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Ej: 11 5580 5083, +54 9 11 5580 5083, 541155805083…"
                            value={newPhone}
                            onChange={(e) => setNewPhone(e.target.value)}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Se guardará y enviará siempre como <code>549XXXXXXXXX</code>.
                        </p>

                        <label className="label mt-4">
                            <span className="label-text">Template a enviar</span>
                        </label>
                        <select
                            className="select select-bordered w-full"
                            value={newTemplate}
                            onChange={(e) => setNewTemplate(e.target.value)}
                        >
                            <option value="nueva_conversacion_phiphitoys">
                                PhiPhiToys (nueva_conversacion_phiphitoys)
                            </option>
                            <option value="nueva_conversacion_renata">
                                Renata (nueva_conversacion_renata)
                            </option>
                        </select>

                        <label className="label mt-4">
                            <span className="label-text">Enviar desde</span>
                        </label>
                        <div className="flex flex-col gap-2">
                            <label className="cursor-pointer flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="fromProfile"
                                    className="radio"
                                    value="consumidor"
                                    checked={newFromProfile === "consumidor"}
                                    onChange={() => setNewFromProfile("consumidor")}
                                />
                                <span className="text-sm">
                                    Consumidor Final &nbsp;
                                    <span className="text-xs text-gray-500">
                                        (5491168404947)
                                    </span>
                                </span>
                            </label>
                            <label className="cursor-pointer flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="fromProfile"
                                    className="radio"
                                    value="mayorista"
                                    checked={newFromProfile === "mayorista"}
                                    onChange={() => setNewFromProfile("mayorista")}
                                />
                                <span className="text-sm">
                                    Mayorista &nbsp;
                                    <span className="text-xs text-gray-500">
                                        (12134250460)
                                    </span>
                                </span>
                            </label>
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn"
                                onClick={() => setShowNewConvModal(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSendNewConversation}
                            >
                                Enviar template
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal ACCIÓN MASIVA / SORTEO */}
            {showMassActionModal && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-xl relative overflow-hidden">
                        <h3 className="font-bold text-xl mb-2 flex items-center gap-2">
                            <span>🎁 Sorteo relámpago</span>
                        </h3>

                        {/* Estado 1: explicación inicial */}
                        {!winnersRevealed && !loadingWinners && (
                            <>
                                <p className="text-sm mb-4 text-gray-600">
                                    Se seleccionaron aleatoriamente hasta{" "}
                                    <span className="font-semibold">10 clientes</span> con
                                    actividad en las últimas{" "}
                                    <span className="font-semibold">18 horas</span>.
                                </p>
                                <p className="text-sm mb-6 text-gray-600">
                                    Hacé clic en{" "}
                                    <span className="font-semibold">“Revelar ganadores”</span>{" "}
                                    para ver el listado del sorteo.
                                </p>

                                <div className="modal-action">
                                    <button
                                        className="btn"
                                        onClick={() => {
                                            setShowMassActionModal(false);
                                            setWinnersRevealed(false);
                                            setLoadingWinners(false);
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            setLoadingWinners(true);
                                            setTimeout(() => {
                                                setWinnersRevealed(true);
                                                setLoadingWinners(false);
                                            }, 3000);
                                        }}
                                    >
                                        Revelar ganadores
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Estado 2: loader */}
                        {loadingWinners && (
                            <div className="flex flex-col items-center justify-center py-8 gap-3">
                                <span className="loading loading-spinner loading-lg" />
                                <p className="text-sm text-gray-600">
                                    Cargando ganadores...
                                </p>
                            </div>
                        )}

                        {/* Estado 3: ganadores revelados */}
                        {winnersRevealed && !loadingWinners && (
                            <>
                                <p className="text-sm mb-3 text-gray-600">
                                    ¡Ganadores revelados! 🎉 El puesto{" "}
                                    <span className="font-bold">#1</span> es el{" "}
                                    <span className="font-bold">Ganador</span> y los
                                    demás son <span className="font-bold">Suplentes</span>.
                                </p>

                                {sampledClients.length ? (
                                    <div className="overflow-x-auto max-h-80 mb-4">
                                        <table className="table table-zebra w-full">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Nombre</th>
                                                    <th>Teléfono</th>
                                                    <th>Rol</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sampledClients.map((c, idx) => {
                                                    const isMainWinner = idx === 0;
                                                    return (
                                                        <tr
                                                            key={c.id}
                                                            className={
                                                                isMainWinner
                                                                    ? "bg-yellow-200 text-yellow-900 font-bold"
                                                                    : ""
                                                            }
                                                        >
                                                            <td>{idx + 1}</td>
                                                            <td>{c.name}</td>
                                                            <td>
                                                                <code>{c.phone}</code>
                                                            </td>
                                                            <td>
                                                                {isMainWinner ? (
                                                                    <span className="badge badge-warning">
                                                                        Ganador
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge badge-outline">
                                                                        Suplente
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-500 mb-4">
                                        No se encontraron candidatos.
                                    </div>
                                )}

                                <div className="modal-action">
                                    <button
                                        className="btn"
                                        onClick={() => {
                                            setShowMassActionModal(false);
                                            setWinnersRevealed(false);
                                            setLoadingWinners(false);
                                        }}
                                    >
                                        Cerrar
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal MENSAJE MASIVO */}
            {/* Modal MENSAJE MASIVO */}
            {showMassMessageModal && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-xl">
                        <h3 className="font-bold text-xl mb-3">
                            ✉️ Mensaje masivo
                        </h3>

                        <p className="text-sm mb-3 text-gray-600">
                            Escribí un mensaje de texto para enviar por WhatsApp
                            a todas las personas que hablaron en las últimas{" "}
                            <strong>18 horas</strong>.
                        </p>

                        <label className="label">
                            <span className="label-text">Mensaje</span>
                        </label>
                        <textarea
                            className="textarea textarea-bordered w-full min-h-[120px]"
                            placeholder="Escribí aquí el mensaje que querés enviar..."
                            value={massMessageText}
                            onChange={(e) => setMassMessageText(e.target.value)}
                        />

                        <div className="modal-action">
                            <button
                                className="btn"
                                onClick={() => {
                                    setShowMassMessageModal(false);
                                    setMassMessageSending(false);
                                }}
                                disabled={massMessageSending}
                            >
                                Cancelar
                            </button>
                            <button
                                className={`btn btn-accent ${massMessageSending ? "btn-disabled" : ""
                                    }`}
                                onClick={handleSendMassMessage}
                                disabled={massMessageSending}
                            >
                                {massMessageSending && (
                                    <span className="loading loading-spinner loading-xs mr-2" />
                                )}
                                {massMessageSending
                                    ? "Enviando..."
                                    : "Enviar mensaje"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
}
